import * as Sentry from '@sentry/react';

const dsn = String(import.meta.env.VITE_SENTRY_DSN || '').trim();
const enableInDev = import.meta.env.VITE_SENTRY_ENABLE_IN_DEV === 'true';
const sentryEnabled = Boolean(dsn) && (import.meta.env.PROD || enableInDev);
const environment = String(
  import.meta.env.VITE_SENTRY_ENVIRONMENT || (import.meta.env.DEV ? 'development' : 'production')
).trim();

let initialized = false;
let consolePatched = false;
let consoleCaptureInFlight = false;

const capturedErrors = new WeakSet<Error>();

const authUserState: { id?: string; has_phone?: boolean } = {};
const runtimeUserState: { has_phone?: boolean } = {};

import {
  SENTRY_TRACE_PROPAGATION_TARGETS,
  createSentryTransactionPrivacyIntegration,
  redactPiiString,
  sanitizeLogArg,
  sanitizeLogArgs,
  sanitizeRoutePath,
  sanitizeSentryBreadcrumb,
  sanitizeSentryErrorEvent,
  sanitizeSentryLogEvent,
  sanitizeSentrySpanData,
  sanitizeSentryTransactionEvent,
  sanitizeSpanAttributes,
  sanitizeUrlForTelemetry,
  serializeConsoleArg,
} from './sentryLogSanitizer.mjs';

export {
  SENTRY_TRACE_PROPAGATION_TARGETS,
  createSentryTransactionPrivacyIntegration,
  redactPiiString,
  sanitizeLogArg,
  sanitizeLogArgs,
  sanitizeRoutePath,
  sanitizeSentryBreadcrumb,
  sanitizeSentryErrorEvent,
  sanitizeSentryLogEvent,
  sanitizeSentrySpanData,
  sanitizeSentryTransactionEvent,
  sanitizeSpanAttributes,
  sanitizeUrlForTelemetry,
  serializeConsoleArg,
};

const applyUserState = () => {
  if (!authUserState.id) {
    Sentry.setUser(null);
  } else {
    Sentry.setUser({ id: authUserState.id });
  }

  const hasPhone = Boolean(authUserState.has_phone || runtimeUserState.has_phone);
  Sentry.setTag('has_user_phone', hasPhone ? 'true' : 'false');
};

const shouldIgnoreConsoleError = (args: unknown[]) => {
  const message = args.map(serializeConsoleArg).join(' ').toLowerCase();
  return (
    !message ||
    message.includes('resizeobserver loop limit exceeded') ||
    message.includes('non-error promise rejection captured') ||
    message.includes('chrome-extension://')
  );
};

const toError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  return new Error(typeof error === 'string' ? error : serializeConsoleArg(error) || 'Unknown error');
};

const wasCaptured = (error: Error) => {
  if (capturedErrors.has(error)) return true;
  capturedErrors.add(error);
  return false;
};

const patchConsoleError = () => {
  if (consolePatched || !sentryEnabled) return;
  consolePatched = true;

  const originalConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    originalConsoleError(...args);

    if (consoleCaptureInFlight || shouldIgnoreConsoleError(args)) return;

    const errorArg = args.find((arg) => arg instanceof Error);
    const error = errorArg instanceof Error ? errorArg : null;
    if (error && wasCaptured(error)) return;

    consoleCaptureInFlight = true;
    try {
      Sentry.withScope((scope) => {
        const sanitizedArgs = sanitizeLogArgs(args);
        scope.setTag('handled_error.source', 'console.error');
        scope.setContext('console', {
          argCount: args.length,
          preview: sanitizedArgs.map(serializeConsoleArg).slice(0, 5),
        });

        if (error) {
          Sentry.captureException(sanitizeLogArg(error) as Error);
          return;
        }

        const message = sanitizedArgs.map(serializeConsoleArg).filter(Boolean).join(' ').slice(0, 1000);
        if (message) {
          Sentry.captureMessage(message, 'error');
        }
      });
    } finally {
      consoleCaptureInFlight = false;
    }
  };
};

const sanitizeUrlString = (urlStr: unknown): string => {
  return sanitizeUrlForTelemetry(urlStr);
};

const parseSampleRate = (value: unknown, fallback: number): number => {
  const raw = String(value ?? '').trim();
  if (!raw) return fallback;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(1, Math.max(0, parsed));
};

export const initializeSentry = () => {
  if (initialized) return;
  initialized = true;

  if (!sentryEnabled) return;

  const tracesSampleRate = parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.2);

  Sentry.init({
    dsn,
    enabled: sentryEnabled,
    environment,
    sendDefaultPii: false,
    attachStacktrace: true,
    enableLogs: sentryEnabled,
    integrations: [
      Sentry.consoleLoggingIntegration({
        levels: ['log', 'info', 'warn', 'error'],
      }),
      createSentryTransactionPrivacyIntegration(),
      ...(typeof (Sentry as any).browserTracingIntegration === 'function'
        ? [
            (Sentry as any).browserTracingIntegration({
              beforeStartSpan: (context: any) => ({
                ...context,
                name: sanitizeUrlForTelemetry(context?.name || ''),
              }),
            }),
          ]
        : []),
    ],
    tracePropagationTargets: SENTRY_TRACE_PROPAGATION_TARGETS,
    tracesSampleRate,
    beforeBreadcrumb(breadcrumb) {
      if (!sentryEnabled) return null;
      if (breadcrumb.data?.url) {
        breadcrumb.data.url = sanitizeUrlString(breadcrumb.data.url);
      }
      if (breadcrumb.data?.from) {
        breadcrumb.data.from = sanitizeUrlString(breadcrumb.data.from);
      }
      if (breadcrumb.data?.to) {
        breadcrumb.data.to = sanitizeUrlString(breadcrumb.data.to);
      }
      if (breadcrumb.category === 'navigation' && breadcrumb.message) {
        breadcrumb.message = sanitizeUrlString(breadcrumb.message);
      }
      return sanitizeSentryBreadcrumb(breadcrumb);
    },
    beforeSend(event) {
      if (!sentryEnabled) return null;
      if (event.request?.url) {
        event.request.url = sanitizeUrlString(event.request.url);
      }
      if (event.user) {
        delete (event.user as any).phone_number;
        delete (event.user as any).phoneNumber;
      }
      return sanitizeSentryErrorEvent(event);
    },
    beforeSendTransaction(event: any) {
      if (!sentryEnabled) return null;
      return sanitizeSentryTransactionEvent(event);
    },
    beforeSendSpan(span: any) {
      return sanitizeSentrySpanData(span);
    },
    beforeSendLog(log: any) {
      if (!sentryEnabled) return null;
      return sanitizeSentryLogEvent(log);
    },
  });

  patchConsoleError();
  applyUserState();
};

export const setSentryAuthUser = (user: { id?: string | null; phoneNumber?: string | null } | null) => {
  authUserState.id = user?.id || undefined;
  authUserState.has_phone = Boolean(user?.phoneNumber);
  if (!sentryEnabled) return;
  applyUserState();
};

export const setSentryRuntimePhone = (phone: string | null | undefined) => {
  runtimeUserState.has_phone = Boolean(phone);
  if (!sentryEnabled) return;
  applyUserState();
};

export const clearSentryRuntimePhone = () => {
  runtimeUserState.has_phone = false;
  if (!sentryEnabled) return;
  applyUserState();
};

export const setSentryRoute = (route: string) => {
  if (!sentryEnabled) return;
  const cleanPath = sanitizeRoutePath(String(route || '/')) || '/';
  Sentry.setTag('route', cleanPath);
  Sentry.setContext('route', { path: cleanPath });
  Sentry.addBreadcrumb({
    category: 'navigation',
    message: cleanPath,
    level: 'info',
  });
};

export const captureHandledException = (error: unknown, extras?: Record<string, unknown>) => {
  if (!sentryEnabled) return;
  const normalizedError = toError(error);
  if (wasCaptured(normalizedError)) return;

  Sentry.withScope((scope) => {
    scope.setTag('handled_error.source', 'manual_capture');
    if (extras) {
      for (const [key, value] of Object.entries(extras)) {
        scope.setExtra(key, value);
      }
    }
    Sentry.captureException(normalizedError);
  });
};

export const sentryIsEnabled = sentryEnabled;

export const logClientInfo = (...args: any[]): void => {
  const sanitized = sanitizeLogArgs(args);
  if (import.meta.env.DEV) {
    console.log(...sanitized);
    return;
  }
  const message = sanitized.map(serializeConsoleArg).filter(Boolean).join(' ');
  if (message) {
    Sentry.addBreadcrumb({
      category: 'client.log',
      message: redactPiiString(message),
      level: 'info',
    });
    try {
      if (typeof (Sentry as any).logger?.info === 'function') {
        if (
          sanitized.length === 2 &&
          typeof sanitized[0] === 'string' &&
          sanitized[1] &&
          typeof sanitized[1] === 'object' &&
          !Array.isArray(sanitized[1]) &&
          !(sanitized[1] instanceof Error)
        ) {
          (Sentry as any).logger.info(sanitized[0], sanitized[1] as Record<string, any>);
        } else {
          (Sentry as any).logger.info(message);
        }
      }
    } catch {}
  }
};

export const logClientWarning = (...args: any[]): void => {
  const sanitized = sanitizeLogArgs(args);
  if (import.meta.env.DEV) {
    console.warn(...sanitized);
    return;
  }
  const message = sanitized.map(serializeConsoleArg).filter(Boolean).join(' ');
  if (message) {
    Sentry.addBreadcrumb({
      category: 'client.warn',
      message: redactPiiString(message),
      level: 'warning',
    });
  }
  console.warn(...sanitized);
};

export const logClientError = (...args: any[]): void => {
  const sanitized = sanitizeLogArgs(args);
  if (import.meta.env.DEV) {
    console.error(...sanitized);
    return;
  }
  const message = sanitized.map(serializeConsoleArg).filter(Boolean).join(' ');
  if (message) {
    Sentry.addBreadcrumb({
      category: 'client.error',
      message: redactPiiString(message),
      level: 'error',
    });
  }
  console.error(...sanitized);
};

export const logClientDebug = (...args: any[]): void => {
  if (import.meta.env.DEV) {
    console.debug(...args);
  }
};

export const sentryLogger = {
  info: (message: string, attributes?: Record<string, any>) => {
    const cleanMessage = redactPiiString(message);
    const cleanAttrs = attributes ? (sanitizeLogArg(attributes) as Record<string, any>) : undefined;
    if (import.meta.env.DEV) {
      if (cleanAttrs !== undefined) {
        console.log(cleanMessage, cleanAttrs);
      } else {
        console.log(cleanMessage);
      }
    } else {
      const breadcrumbMsg = cleanAttrs ? `${cleanMessage} ${serializeConsoleArg(cleanAttrs)}` : cleanMessage;
      if (breadcrumbMsg) {
        Sentry.addBreadcrumb({
          category: 'client.log',
          message: redactPiiString(breadcrumbMsg),
          level: 'info',
        });
      }
    }
    try {
      if (typeof (Sentry as any).logger?.info === 'function') {
        (Sentry as any).logger.info(cleanMessage, cleanAttrs);
      }
    } catch {}
  },
  warn: (message: string, attributes?: Record<string, any>) => {
    const cleanMessage = redactPiiString(message);
    const cleanAttrs = attributes ? (sanitizeLogArg(attributes) as Record<string, any>) : undefined;
    if (import.meta.env.DEV) {
      if (cleanAttrs !== undefined) {
        console.warn(cleanMessage, cleanAttrs);
      } else {
        console.warn(cleanMessage);
      }
    } else {
      const breadcrumbMsg = cleanAttrs ? `${cleanMessage} ${serializeConsoleArg(cleanAttrs)}` : cleanMessage;
      if (breadcrumbMsg) {
        Sentry.addBreadcrumb({
          category: 'client.warn',
          message: redactPiiString(breadcrumbMsg),
          level: 'warning',
        });
      }
    }
    try {
      if (typeof (Sentry as any).logger?.warn === 'function') {
        (Sentry as any).logger.warn(cleanMessage, cleanAttrs);
      }
    } catch {}
  },
  error: (message: string, attributes?: Record<string, any>) => {
    const cleanMessage = redactPiiString(message);
    const cleanAttrs = attributes ? (sanitizeLogArg(attributes) as Record<string, any>) : undefined;
    if (import.meta.env.DEV) {
      if (cleanAttrs !== undefined) {
        console.error(cleanMessage, cleanAttrs);
      } else {
        console.error(cleanMessage);
      }
    } else {
      const breadcrumbMsg = cleanAttrs ? `${cleanMessage} ${serializeConsoleArg(cleanAttrs)}` : cleanMessage;
      if (breadcrumbMsg) {
        Sentry.addBreadcrumb({
          category: 'client.error',
          message: redactPiiString(breadcrumbMsg),
          level: 'error',
        });
      }
    }
    try {
      if (typeof (Sentry as any).logger?.error === 'function') {
        (Sentry as any).logger.error(cleanMessage, cleanAttrs);
      }
    } catch {}
  },
};

export interface SentrySpanOptions {
  name: string;
  op: string;
  attributes?: Record<string, any>;
}

export const withSentrySpan = <T>(
  options: SentrySpanOptions,
  callback: () => T
): T => {
  const cleanName = redactPiiString(String(options.name || 'custom.operation'));
  const cleanOp = String(options.op || 'function');
  const cleanAttributes = sanitizeSpanAttributes(options.attributes);
  if (typeof (Sentry as any).startSpan === 'function') {
    return (Sentry as any).startSpan(
      {
        name: cleanName,
        op: cleanOp,
        ...(cleanAttributes ? { attributes: cleanAttributes } : {}),
      },
      () => callback()
    );
  }
  return callback();
};
